import {
  InteractionRequiredAuthError,
  IPublicClientApplication,
  AccountInfo,
} from '@azure/msal-browser'
import { apiScope, globalConfig } from '../../../configuration/config'
import { ILoginRequest } from '../types'
import { API_URLs } from '../../../configuration/api-urls'
import { ActivityTypes } from '../../../pages/constants'
import * as jose from 'jose'

const textDecoder = new TextDecoder()

const SESSION_KEY_STORAGE_KEY = 'tokenEncryptionKey'

// AES-256 key generated per browser session. It is sent to the API wrapped
// with the RSA public key, so it never has to be shipped in configuration.
const getEncryptionKey = (): Uint8Array => {
  const storedKey = sessionStorage.getItem(SESSION_KEY_STORAGE_KEY)
  if (storedKey) {
    return new Uint8Array(str2ab(window.atob(storedKey)))
  }

  const key = window.crypto.getRandomValues(new Uint8Array(32))
  sessionStorage.setItem(
    SESSION_KEY_STORAGE_KEY,
    window.btoa(ab2str(key.buffer))
  )
  // Tokens stored before this key existed can no longer be decrypted
  sessionStorage.removeItem('idToken')
  sessionStorage.removeItem('encryptedToken')
  sessionStorage.removeItem('encryptedAESKey')
  return key
}

const getPublicKey = () => {
  return globalConfig.get().publicKey
}

export const decryptToken = async (
  encryptedToken: string | null
): Promise<string> => {
  if (!encryptedToken) {
    throw new Error('Encrypted token is missing or invalid.')
  }

  try {
    const keyBytes = getEncryptionKey()

    const plainText = await jose
      .compactDecrypt(encryptedToken, keyBytes)
      .then((result) => result.plaintext)

    const decryptedData = JSON.parse(textDecoder.decode(plainText))
    const decryptedToken = decryptedData?.token
    return decryptedToken
  } catch (error) {
    console.error('Error decrypting token:', error)
    throw error
  }
}

const encryptToken = async (token: string, key: Uint8Array) => {
  const jwe = await new jose.EncryptJWT({ token })
    .setProtectedHeader({ alg: 'dir', enc: 'A256GCM' })
    .encrypt(key)
  return jwe
}

const convertKeyToCrypto = async (
  encryptionKey: Uint8Array
): Promise<CryptoKey> => {
  // Encrypt the data with the AES key
  const cryptoKey = await window.crypto.subtle.importKey(
    'raw',
    encryptionKey.buffer as ArrayBuffer,
    'AES-GCM',
    true,
    ['encrypt', 'decrypt']
  )
  return cryptoKey
}

async function encryptSymmetricKeyWithRSA(
  publicKey: CryptoKey,
  symmetricKey: CryptoKey
): Promise<ArrayBuffer> {
  const exportedKey = await window.crypto.subtle.exportKey('raw', symmetricKey)
  return await window.crypto.subtle.encrypt(
    {
      name: 'RSA-OAEP',
    },
    publicKey,
    exportedKey
  )
}

async function importPublicKeyAndEncrypt(
  token: string
): Promise<{ encryptedAESKey: string; encryptedToken: string }> {
  try {
    const publicKey = getPublicKey()
    const pub = await importPublicKey(publicKey)
    const encryptionKey = getEncryptionKey()

    // Encrypt the data with the AES key (returns JWE compact serialization)
    const encryptedToken = await encryptToken(token, encryptionKey)

    // Encrypt the AES key with the RSA public key
    const cryptoKey = await convertKeyToCrypto(encryptionKey)
    const encryptedKey = await encryptSymmetricKeyWithRSA(pub, cryptoKey)

    // Backend expects base64-encoded JWE string
    try {
      const encodedToken = window.btoa(encryptedToken)
      const encodedKey = window.btoa(ab2str(encryptedKey))

      return {
        encryptedAESKey: encodedKey,
        encryptedToken: encodedToken,
      }
    } catch (btoaError) {
      console.error('btoa encoding failed:', btoaError)
      console.error('encryptedToken type:', typeof encryptedToken)
      console.error('encryptedToken length:', encryptedToken?.length)
      throw new Error('Failed to base64 encode encrypted token')
    }
  } catch (error) {
    console.error('Full error object:', error)
    throw new Error('Encryption failed')
  }
}

function str2ab(str: string): ArrayBuffer {
  const buf = new ArrayBuffer(str.length)
  const bufView = new Uint8Array(buf)
  for (let i = 0; i < str.length; i++) {
    bufView[i] = str.charCodeAt(i)
  }
  return buf
}

function ab2str(buf: ArrayBuffer): string {
  return String.fromCharCode(...Array.from(new Uint8Array(buf)))
}

async function importPublicKey(spkiPem: string): Promise<CryptoKey> {
  const key = await window.crypto.subtle.importKey(
    'spki',
    getSpkiDer(spkiPem),
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256',
    },
    true,
    ['encrypt']
  )
  return key
}

function getSpkiDer(spkiPem: string): ArrayBuffer {
  const pemHeader = '-----BEGIN PUBLIC KEY-----'
  const pemFooter = '-----END PUBLIC KEY-----'
  const pemContents = spkiPem.substring(
    pemHeader.length,
    spkiPem.length - pemFooter.length
  )
  const binaryDerString = window.atob(pemContents)
  return str2ab(binaryDerString)
}

async function RequestToken(
  instance: IPublicClientApplication,
  accounts: AccountInfo[]
): Promise<string> {
  const postPeerActivityUrl = globalConfig
    .get()
    .apiBaseUrl.concat(API_URLs.v1.POST_PeerSupporterActivity)

  const postLoginActivity = async (
    encryptedKey: string,
    encryptedData: string
  ): Promise<unknown> => {
    try {
      const response = await fetch(postPeerActivityUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          EncryptedAESKey: encryptedKey,
          EncryptedToken: encryptedData,
        },
        body: JSON.stringify({ activityType: ActivityTypes.LOGIN }),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Unable to fetch notifications - ', error)
    }
  }

  let accessToken = ''
  let idToken = ''
  if (accounts.length > 0) {
    // Debug: Log MSAL account object to understand what email is used for SSO
    console.log('MSAL Account object for SSO:', {
      username: accounts[0].username,
      name: accounts[0].name,
      localAccountId: accounts[0].localAccountId,
      environment: accounts[0].environment,
    })

    const loginRequest: ILoginRequest = {
      scopes: ['User.Read', 'User.Read.All', apiScope],
    }

    const request = {
      ...loginRequest,
      account: accounts[0],
    }

    try {
      const response = await instance.acquireTokenSilent(request)
      accessToken = response.accessToken
      idToken = response.idToken

      const encryptionKey = getEncryptionKey()
      const encryptedSymmetricToken = await encryptToken(idToken, encryptionKey)
      sessionStorage.setItem('idToken', encryptedSymmetricToken)

      const { encryptedAESKey, encryptedToken } =
        await importPublicKeyAndEncrypt(idToken)

      sessionStorage.setItem('encryptedToken', encryptedToken)
      sessionStorage.setItem('encryptedAESKey', encryptedAESKey)

      await postLoginActivity(encryptedAESKey, encryptedToken) // Use encrypted data for activity
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        const response = await instance.acquireTokenPopup(request)
        accessToken = response.accessToken
        idToken = response.idToken

        const encryptionKey = getEncryptionKey()
        const encryptedSymmetricToken = await encryptToken(
          idToken,
          encryptionKey
        )
        sessionStorage.setItem('idToken', encryptedSymmetricToken)

        const { encryptedAESKey, encryptedToken } =
          await importPublicKeyAndEncrypt(idToken)

        sessionStorage.setItem('encryptedToken', encryptedToken)
        sessionStorage.setItem('encryptedAESKey', encryptedAESKey)
        await postLoginActivity(encryptedAESKey, encryptedToken) // Use encrypted data for activity
      }
    }
  }

  return accessToken
}

/**
 * Helper function to encrypt and store token in session storage
 */
export async function encryptAndStoreToken(idToken: string): Promise<void> {
  // Ensure we have fresh config with the RSA public key
  const config = globalConfig.get()
  if (!config || !config.publicKey) {
    console.error('Missing encryption keys in config')
    throw new Error('Encryption configuration is missing')
  }

  const encryptionKey = getEncryptionKey()
  const encryptedSymmetricToken = await encryptToken(idToken, encryptionKey)
  sessionStorage.setItem('idToken', encryptedSymmetricToken)

  const { encryptedAESKey, encryptedToken } = await importPublicKeyAndEncrypt(
    idToken
  )

  sessionStorage.setItem('encryptedToken', encryptedToken)
  sessionStorage.setItem('encryptedAESKey', encryptedAESKey)
}

export default RequestToken
